// Story 1.26 — Applications panel for /account/creator-dashboard.
//
// Combines the creator-application summary card (Story 1.21 source)
// + the portfolio-applications list (Story 1.24 source) under one
// surface. Read-only: the Withdraw button stays at
// /account/creator-portfolios (Story 1.25's home); the dashboard
// row's "Manage" link deep-links back via #application-{id} anchor
// (Story 1.24's `MyApplicationRow` carries the id attribute).
//
// PURE-CLIENT FILTERING:
//
// Status-filter chips operate on the already-fetched array (no
// refetch). The dashboard endpoint returns the full unfiltered list
// capped at 100 (`APPLICATIONS_TAB_MAX_PORTFOLIO_APPS`). For a typical
// creator with ≤100 apps this is <10 KB — re-fetching per filter
// change would be wasteful. Differs from Story 1.24's
// `MyApplicationsPanel` (which re-fetches against
// /portfolios/applications/me?status=… — that surface's purpose is
// "deep drill into one status"; this surface's purpose is "single
// overview").

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@greenhold/components";

import type {
  ApplicationsPanelData,
  CreatorApplicationSummary,
  PortfolioApplicationSummary,
} from "../../features/creator-dashboard/useCreatorDashboard";
import {
  PORTFOLIO_APPLICATION_STATUS_FILTERS,
  type MyApplicationsStatusFilter,
  statusBadgeVariant,
} from "../../lib/portfolio-status-badge";

const BECOME_CREATOR_PATH = "/account/become-creator";
const CREATOR_PORTFOLIOS_PATH = "/account/creator-portfolios";

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString();
}

export function ApplicationsPanel({ data }: { data: ApplicationsPanelData }) {
  return (
    <div className="space-y-6">
      {data.creator_application !== null ? (
        <CreatorApplicationCard summary={data.creator_application} />
      ) : (
        <Card>
          <CardContent className="py-6 text-center opacity-70">
            No creator application on file. (Your account was approved
            directly; this should only happen for admin-seeded creators.)
          </CardContent>
        </Card>
      )}

      <section>
        <h3 className="text-lg font-heading font-semibold mb-4">
          Portfolio applications
        </h3>
        <PortfolioApplicationsList rows={data.portfolio_applications} />
      </section>
    </div>
  );
}

function CreatorApplicationCard({
  summary,
}: {
  summary: CreatorApplicationSummary;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle>Creator application</CardTitle>
            <CardDescription>
              Your application to become an approved Greenhold creator.
            </CardDescription>
          </div>
          <Badge variant={statusBadgeVariant(summary.status)}>
            {summary.status}
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
          <dt className="opacity-70">Submitted</dt>
          <dd>{formatDate(summary.submitted_at)}</dd>
          <dt className="opacity-70">Reviewed</dt>
          <dd>{formatDate(summary.reviewed_at)}</dd>
        </dl>
        {summary.rejection_reason ? (
          <blockquote className="opacity-80 border-l-2 border-brand-forest-300 pl-3 mt-4 text-sm">
            {summary.rejection_reason}
          </blockquote>
        ) : null}
        {summary.can_resubmit ? (
          <p className="mt-4">
            <Link
              to={BECOME_CREATOR_PATH}
              className="text-sm underline decoration-dotted"
            >
              Re-apply →
            </Link>
          </p>
        ) : null}
      </CardContent>
    </Card>
  );
}

function PortfolioApplicationsList({
  rows,
}: {
  rows: PortfolioApplicationSummary[];
}) {
  const [statusFilter, setStatusFilter] =
    useState<MyApplicationsStatusFilter>("any");

  const filtered = useMemo(() => {
    if (statusFilter === "any") return rows;
    return rows.filter((r) => r.status === statusFilter);
  }, [rows, statusFilter]);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {PORTFOLIO_APPLICATION_STATUS_FILTERS.map((s) => (
          <Button
            key={s}
            variant={statusFilter === s ? "primary" : "tertiary"}
            size="sm"
            onClick={() => setStatusFilter(s)}
            aria-pressed={statusFilter === s}
          >
            {s === "any" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>

      {rows.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-center opacity-70">
            No portfolio applications yet.{" "}
            <Link
              to={CREATOR_PORTFOLIOS_PATH}
              className="underline decoration-dotted"
            >
              Browse recruiting portfolios →
            </Link>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="py-6 text-center opacity-70">
            No applications match the selected filter.
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3">
          {filtered.map((row) => (
            <DashboardPortfolioApplicationRow key={row.id} application={row} />
          ))}
        </div>
      )}
    </div>
  );
}

function DashboardPortfolioApplicationRow({
  application,
}: {
  application: PortfolioApplicationSummary;
}) {
  return (
    <Card>
      <CardContent className="py-4 flex items-center justify-between gap-4">
        <div>
          <p className="font-medium">{application.portfolio_title}</p>
          {application.portfolio_theme ? (
            <p className="text-sm opacity-70">{application.portfolio_theme}</p>
          ) : null}
          <p className="text-xs opacity-60 mt-1">
            Submitted {formatDate(application.submitted_at)}
            {application.reviewed_at
              ? ` · Reviewed ${formatDate(application.reviewed_at)}`
              : ""}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={statusBadgeVariant(application.status)}>
            {application.status}
          </Badge>
          <Link
            to={`${CREATOR_PORTFOLIOS_PATH}#application-${application.id}`}
            className="text-sm underline decoration-dotted"
            aria-label={`Manage application for ${application.portfolio_title}`}
          >
            Manage →
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
